/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-sort-props */
import * as Select from '@radix-ui/react-select';
import clx from 'classnames';
import { useController, useFormContext } from 'react-hook-form';
import PhoneInput, { DefaultInputComponentProps, FlagProps, Props } from 'react-phone-number-input';
import FieldLayout from '../../FieldLayout';
import { ErrorLabel, Label } from '../../labels';
import SelectItem from '../../Select/SelectItem';
import HTMLInput from '../Text/HTMLInput';

type PhoneNumberProps = Omit<Props<DefaultInputComponentProps>, 'onChange'>;

type CountrySelectComponent = {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
};

const flagUrl = (country: string) => `https://purecatamphetamine.github.io/country-flag-icons/3x2/${country}.svg`;

const Flag = ({ countryName, country }: FlagProps) => <img src={flagUrl(country)} alt={countryName} />;

const PhoneNumber = ({ disabled, className = '', required, name, label, ...props }: PhoneNumberProps) => {
  const { control, formState } = useFormContext();
  const { errors } = formState;
  const error = errors[name];
  const { field } = useController({
    name,
    control,
  });

  return (
    <FieldLayout>
      {label && <Label disabled={disabled} label={label} name={name} required={required} />}
      <PhoneInput
        className={`
          w-full flex flex-row
          ${error ? 'border-red-500' : 'border-gray-300'}
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
          ${className}
        `}
        countrySelectComponent={({ options, value, onChange, ...props }: CountrySelectComponent) => (
          <Select.Root {...props} value={value} dir="ltr" onValueChange={onChange}>
            <Select.Trigger
              className={clx(
                'group text-neutral-800 placeholder:text-neutral-100 lg-regular flex items-center justify-between',
                'autofill:bg-neutral-100 bg-mercury-100 rounded-tl-lg rounded-bl-lg border px-3 py-2',
                'hover:bg-neutral-50 transition-colors duration-quick ease-out',
                `focus:border-neutral-50 focus:bg-neutral-100
          data-[state=open]:border-primary-300`,
                `disabled:bg-system-disabled-50 disabled:autofill:bg-system-disabled-50
        disabled:border-system-disabled-300 disabled:text-system-disabled-300`,
              )}
            >
              <Select.Value>
                <div className="flex items-center gap-2 w-16">
                  {value}
                  {value && <img alt="" className="h-4" src={flagUrl(value)} />}
                </div>
              </Select.Value>
              <Select.Icon className="SelectIcon">
                <i
                  className={`fa-solid fa-caret-down text-neutral-500 group-disabled:text-system-disabled-300
        group-data-[state=open]:rotate-180 transition-transform duration-quick ease-out`}
                />
              </Select.Icon>
            </Select.Trigger>
            <Select.Portal>
              <Select.Content
                className="z-[100] bg-neutral-50 border border-neutral-100 rounded-lg shadow-raised max-h-52 w-80"
                position="popper"
                asChild
              >
                <Select.Viewport className="px-1.5 py-0.5">
                  {options.map(({ value, label }) => (
                    <SelectItem key={value} option={{ label }} value={value} />
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Portal>
          </Select.Root>
        )}
        flagComponent={Flag}
        inputComponent={HTMLInput}
        numberInputProps={{
          className: 'rounded-tl-none rounded-bl-none rounded-tr-lg rounded-br-lg border-red-50 border-2',
        }}
        defaultCountry="FR"
        disabled={disabled}
        international
        {...props}
        value={field.value}
        onChange={field.onChange}
      />
      {!!error && <ErrorLabel error={error.message} />}
    </FieldLayout>
  );
};

export default PhoneNumber;
